import Vue from "vue";
import { storeGet } from "@/store/store-utils";
import { ACCRUALS_API } from "../utils/constants";
import { formatGlOptionLabel } from "../utils/accrualsFormat";
import {
  buildBulkPartialFailureMessage,
  estimateRefLabel,
  validateRejectReason,
} from "../views/Automation/Accruals/accrualsEstimatesConstants";

const API = ACCRUALS_API;
function getAxios() {
  return Vue.axios;
}

function envelopeError(error) {
  const payload = error?.response?.data;
  return payload?.message || error?.message || "Accruals ingestion request failed";
}

function isFlowInProgressError(error) {
  const httpCode = error?.response?.status;
  const message = envelopeError(error);
  return (
    httpCode === 409 ||
    /ingestion flow is already in progress/i.test(message) ||
    /flow is already in progress/i.test(message) ||
    /IM selection is running/i.test(message) ||
    /Purchase Order sync is still in progress/i.test(message)
  );
}

function unwrap(response) {
  const body = response?.data;
  if (body && Object.prototype.hasOwnProperty.call(body, "data")) return body.data;
  return body;
}

function resolveCid(rootState) {
  const raw = rootState.client || rootState.user?.client || "";
  if (raw && typeof raw === "object") return raw._id || raw.id || "";
  return raw;
}

export default {
  namespaced: true,
  state: {
    summary: {},
    flowStatus: {},
    rows: [],
    columns: [],
    total: 0,
    filterOptions: {},
    loading: false,
    error: null,
    normalization: null,
    savingMapping: false,
    imRule: null,
    imRuleLoading: false,
    imRuleSaving: false,
    imPreview: null,
    imPreviewLoading: false,
    imSelection: null,
    imSelectionLoading: false,
    estimates: [],
    estimatesTotal: 0,
    estimatesCounts: {
      all: 0,
      pending_submission: 0,
      pending_approval: 0,
      approved: 0,
      rejected: 0,
      excluded: 0,
    },
    estimatesPage: 1,
    estimatesLimit: 25,
    estimatesLoading: false,
    schedule: { rows: [], status: null, prepared_at: null },
    scheduleLoading: false,
    rerunFreshness: {
      stale: false,
      last_agent_run_at: null,
      sources: [],
      affected_count: 0,
      run_reverts_inference_and_computation: false,
    },
    computationRules: [],
    computationTemplates: [],
    selectedRuleId: null,
    computationRuleSaving: false,
    computationRuleLoading: false,
    computationRun: null,
    glOptions: [],
    locationMaster: [],
    access: {
      persona: "manager",
      personas: ["manager"],
      canConfigure: true,
      canIngest: true,
      canRunEstimation: true,
      canReviewEstimates: false,
      canUnlockEstimates: true,
      canOpenWorkbench: false,
      locationKeys: [],
      reviewLocationKeys: [],
      projectId: null,
    },
  },
  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },
    setSavingMapping(state, value) {
      state.savingMapping = value;
    },
    setError(state, value) {
      state.error = value;
    },
    setSummary(state, value) {
      state.summary = value || {};
    },
    setFlowStatus(state, value) {
      state.flowStatus = value || {};
    },
    setTable(state, { rows, columns, total }) {
      state.rows = rows || [];
      state.columns = columns || [];
      state.total = total || 0;
    },
    setFilterOptions(state, value) {
      state.filterOptions = value || {};
    },
    setNormalization(state, value) {
      state.normalization = value || null;
    },
    setImRule(state, value) {
      state.imRule = value || null;
    },
    setImRuleLoading(state, value) {
      state.imRuleLoading = value;
    },
    setImRuleSaving(state, value) {
      state.imRuleSaving = value;
    },
    setImPreview(state, value) {
      state.imPreview = value || null;
    },
    setImPreviewLoading(state, value) {
      state.imPreviewLoading = value;
    },
    setImSelection(state, value) {
      state.imSelection = value || null;
    },
    setImSelectionLoading(state, value) {
      state.imSelectionLoading = value;
    },
    setEstimates(state, { rows, total, counts, page, limit }) {
      state.estimates = rows || [];
      state.estimatesTotal = total || 0;
      state.estimatesCounts = {
        all: 0,
        pending_submission: 0,
        pending_approval: 0,
        approved: 0,
        rejected: 0,
        excluded: 0,
        ...(counts || {}),
      };
      state.estimatesPage = page || 1;
      state.estimatesLimit = limit || 25;
    },
    setEstimatesLoading(state, value) {
      state.estimatesLoading = value;
    },
    setSchedule(state, value) {
      state.schedule = {
        rows: value?.rows || [],
        status: value?.status || null,
        prepared_at: value?.prepared_at || null,
        row_count: value?.row_count || 0,
        journal_count: value?.journal_count || 0,
      };
    },
    setScheduleLoading(state, value) {
      state.scheduleLoading = value;
    },
    setRerunFreshness(state, value) {
      state.rerunFreshness = {
        stale: false,
        last_agent_run_at: null,
        sources: [],
        affected_count: 0,
        run_reverts_inference_and_computation: false,
        ...(value || {}),
      };
    },
    setComputationRules(state, value) {
      state.computationRules = value || [];
    },
    setComputationTemplates(state, value) {
      state.computationTemplates = value || [];
    },
    setSelectedRuleId(state, value) {
      state.selectedRuleId = value || null;
    },
    setComputationRuleSaving(state, value) {
      state.computationRuleSaving = value;
    },
    setComputationRuleLoading(state, value) {
      state.computationRuleLoading = value;
    },
    setGlOptions(state, value) {
      state.glOptions = Array.isArray(value) ? value : [];
    },
    setLocationMaster(state, value) {
      state.locationMaster = Array.isArray(value) ? value : [];
    },
    setComputationRun(state, value) {
      state.computationRun = value || null;
    },
    setAccess(state, value) {
      const personas = Array.isArray(value?.personas) && value.personas.length
        ? value.personas
        : [value?.persona || "approver"];
      state.access = {
        persona: value?.persona || personas[0] || "approver",
        personas,
        canConfigure: Boolean(value?.canConfigure),
        canIngest: Boolean(value?.canIngest),
        canRunEstimation: Boolean(value?.canRunEstimation),
        canReviewEstimates: Boolean(value?.canReviewEstimates),
        canUnlockEstimates: Boolean(value?.canUnlockEstimates),
        canOpenWorkbench: Boolean(value?.canOpenWorkbench),
        locationKeys: Array.isArray(value?.locationKeys) ? value.locationKeys : [],
        reviewLocationKeys: Array.isArray(value?.reviewLocationKeys) ? value.reviewLocationKeys : [],
        approveLocationKeys: Array.isArray(value?.approveLocationKeys) ? value.approveLocationKeys : [],
        projectId: value?.projectId || null,
      };
    },
    upsertComputationRule(state, rule) {
      if (!rule || !rule._id) return;
      const id = String(rule._id);
      const idx = state.computationRules.findIndex((r) => String(r._id) === id);
      if (idx >= 0) {
        Vue.set(state.computationRules, idx, rule);
      } else {
        state.computationRules = [...state.computationRules, rule];
      }
    },
    removeComputationRule(state, ruleId) {
      const id = String(ruleId);
      state.computationRules = state.computationRules.filter((r) => String(r._id) !== id);
      if (String(state.selectedRuleId) === id) state.selectedRuleId = null;
    },
  },
  actions: {
    async loadSummary({ commit, rootState }, { projectId, cid }) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const params = { project: projectId };
        if (cid) params.cid = cid;
        const res = await storeGet(getAxios(), rootState, `${API}/sourceSummary`, params);
        commit("setSummary", unwrap(res));
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      } finally {
        commit("setLoading", false);
      }
    },
    async loadMappingFlowStatus(_context, { cid }) {
      const res = await getAxios().get(`${API}/flowStatus`, {
        params: { cid, latest: 1 },
      });
      return unwrap(res) || {};
    },
    async loadFlowStatus({ commit }, { projectId, cid }) {
      try {
        const res = await getAxios().get(`${API}/flowStatus`, {
          params: { project: projectId, cid },
        });
        commit("setFlowStatus", unwrap(res));
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
      }
    },
    async loadSourceData({ commit }, { projectId, cid, entityType, page, limit, filters }) {
      commit("setLoading", true);
      commit("setError", null);
      try {
        const params = {
          project: projectId,
          cid,
          entity_type: entityType,
          page,
          limit,
        };
        if (filters && Object.keys(filters).length) {
          params.filters = JSON.stringify(filters);
        }
        const res = await getAxios().get(`${API}/sourceData`, { params });
        const data = unwrap(res) || {};
        commit("setTable", data);
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      } finally {
        commit("setLoading", false);
      }
    },
    async loadSourceDataFilterOptions({ commit }, { projectId, cid, entityType }) {
      commit("setError", null);
      try {
        const res = await getAxios().get(`${API}/sourceData/filterOptions`, {
          params: { project: projectId, cid, entity_type: entityType },
        });
        const data = unwrap(res) || {};
        commit("setFilterOptions", data.filter_options || {});
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async uploadSource({ commit }, { projectId, cid, entityType, file }) {
      commit("setError", null);
      const formData = new FormData();
      formData.append("file", file);
      if (projectId) formData.append("project", projectId);
      formData.append("entity_type", entityType);
      try {
        const res = await getAxios().post(`${API}/uploadSource?cid=${cid}`, formData);
        commit("sbSuccess", "Source file landed in S3", { root: true });
        return unwrap(res);
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async loadCanonicalFields({ commit }, { cid, entityType }) {
      commit("setError", null);
      try {
        const res = await getAxios().get(`${API}/canonicalFields`, {
          params: { cid, entity_type: entityType },
        });
        return unwrap(res) || {};
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async loadMappingProject({ commit }, { cid, entityType }) {
      commit("setError", null);
      try {
        const res = await getAxios().get(`${API}/mappingProject`, {
          params: { cid, entity_type: entityType },
        });
        return unwrap(res) || {};
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        throw err;
      }
    },
    async loadSourceHeaders({ commit }, { projectId, cid, entityType }) {
      commit("setError", null);
      try {
        const params = { cid, entity_type: entityType };
        if (projectId) params.project = projectId;
        const res = await getAxios().get(`${API}/sourceHeaders`, {
          params,
        });
        return unwrap(res) || {};
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        throw err;
      }
    },
    async startIngestion({ commit }, { projectId, cid, entityType, periodEndDate }) {
      commit("setError", null);
      try {
        const body = {
          entity_type: entityType,
          period_end_date: periodEndDate || null,
        };
        if (projectId) body.project_id = projectId;
        const res = await getAxios().post(`${API}/startIngestion?cid=${cid}`, body);
        const data = unwrap(res) || {};
        commit("sbSuccess", `Started ${data.flow_name || entityType}`, { root: true });
        return data;
      } catch (err) {
        if (isFlowInProgressError(err)) {
          return { skipped: true, reason: "flow_in_progress", message: envelopeError(err) };
        }
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        throw err;
      }
    },
    async loadNormalizationConfig({ commit }, { projectId, cid, entityType }) {
      commit("setError", null);
      try {
        const params = { cid, entity_type: entityType };
        if (projectId) params.project = projectId;
        const res = await getAxios().get(`${API}/normalizationConfig`, { params });
        const data = unwrap(res) || {};
        commit("setNormalization", data);
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async saveNormalizationConfig({ commit }, { projectId, cid, entityType, fieldMappings, sourceKind }) {
      commit("setSavingMapping", true);
      commit("setError", null);
      try {
        const body = {
          entity_type: entityType,
          source_kind: sourceKind || "file_upload",
          field_mappings: fieldMappings,
        };
        if (projectId) body.project_id = projectId;
        const res = await getAxios().post(`${API}/normalizationConfig?cid=${cid}`, body);
        const data = unwrap(res) || {};
        commit("sbSuccess", "Column mapping saved", { root: true });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      } finally {
        commit("setSavingMapping", false);
      }
    },
    async loadImRule({ commit, rootState }, { projectId }) {
      commit("setImRuleLoading", true);
      commit("setError", null);
      try {
        const res = await storeGet(getAxios(), rootState, `${API}/imRule`, {
          project: projectId,
        });
        const data = unwrap(res) || {};
        commit("setImRule", data);
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      } finally {
        commit("setImRuleLoading", false);
      }
    },
    async saveImRule(
      { commit, rootState },
      { projectId, conditions, groups, groupLogic, groupConnectors, ownerResolution, payloadColumns, status, confirm: confirmFlag }
    ) {
      commit("setImRuleSaving", true);
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const body = {
          project_id: projectId,
          owner_resolution: ownerResolution || undefined,
          payload_columns: payloadColumns || undefined,
          status: status || "saved",
        };
        if (confirmFlag) body.confirm = true;
        if (Array.isArray(groups) && groups.length > 0) {
          body.group_logic = groupLogic || "AND";
          if (Array.isArray(groupConnectors) && groupConnectors.length) {
            body.group_connectors = groupConnectors.map((c) =>
              String(c).toUpperCase() === "OR" ? "OR" : "AND"
            );
          }
          body.groups = groups.map((group, index) => ({
            id: group.id || `group-${index + 1}`,
            logic: group.logic || "AND",
            conditions: (group.conditions || []).map((c) => ({
              field: c.field,
              op: c.operator || c.op,
              value: c.value,
            })),
          }));
        } else {
          body.conditions = (conditions || []).map((c) => ({
            field: c.field,
            op: c.operator || c.op,
            value: c.value,
          }));
        }
        const res = await getAxios().put(`${API}/imRule?cid=${cid}`, body);
        const data = unwrap(res) || {};
        commit("setImRule", data);
        commit("sbSuccess", "Info Request rule saved", { root: true });
        return data;
      } catch (err) {
        const details = err?.response?.data?.data;
        const first = Array.isArray(details) ? details[0] : details;
        if (err?.response?.status === 409 && first?.requires_confirmation) {
          return {
            requires_confirmation: true,
            affected_count: first.affected_count,
            message: envelopeError(err),
          };
        }
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        throw err;
      } finally {
        commit("setImRuleSaving", false);
      }
    },
    async previewImRule({ commit, rootState, state }, { projectId, ruleVersion }) {
      commit("setImPreviewLoading", true);
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const version = ruleVersion !== undefined ? ruleVersion : state.imRule?.version;
        const res = await getAxios().post(`${API}/imRule/preview?cid=${cid}`, {
          project_id: projectId,
          rule_version: version || undefined,
        });
        const data = unwrap(res) || {};
        commit("setImPreview", data);
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      } finally {
        commit("setImPreviewLoading", false);
      }
    },
    async startImSelection({ commit, rootState, state }, { projectId, ruleVersion }) {
      commit("setImSelectionLoading", true);
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const version =
          ruleVersion !== undefined
            ? ruleVersion
            : state.imPreview?.rule_version || state.imRule?.version;
        const res = await getAxios().post(`${API}/imSelection/start?cid=${cid}`, {
          project_id: projectId,
          rule_version: version || undefined,
        });
        const data = unwrap(res) || {};
        commit("setImSelection", data);
        commit("sbSuccess", "IM selection queued", { root: true });
        return data;
      } catch (err) {
        if (isFlowInProgressError(err)) {
          const message = envelopeError(err);
          commit("sbError", message, { root: true });
          return { skipped: true, reason: "locked", message };
        }
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        throw err;
      } finally {
        commit("setImSelectionLoading", false);
      }
    },
    async loadImSelectionStatus({ commit, rootState }, { projectId, selectionRunId }) {
      commit("setError", null);
      try {
        const params = { project: projectId };
        if (selectionRunId) params.selection_run_id = selectionRunId;
        const res = await storeGet(getAxios(), rootState, `${API}/imSelection/status`, params);
        const data = unwrap(res) || {};
        commit("setImSelection", data);
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        throw err;
      }
    },
    async loadAccess({ commit, rootState }, { projectId }) {
      if (!projectId) return null;
      try {
        const res = await storeGet(getAxios(), rootState, `${API}/access`, { project: projectId });
        const data = unwrap(res) || {};
        commit("setAccess", { ...data, projectId });
        return data;
      } catch (err) {
        console.log("[accruals]: loadAccess failed", err);
        return null;
      }
    },
    async approveEstimates({ commit, rootState, dispatch }, { projectId, items }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/estimates/approve?cid=${cid}`, {
          project_id: projectId,
          items,
        });
        const data = unwrap(res) || {};
        const failed = data.failed || data.failures || [];
        if (failed.length) {
          const message = buildBulkPartialFailureMessage(
            failed.map((entry) => entry?.reference || estimateRefLabel(entry))
          );
          commit("sbError", message, { root: true });
        } else {
          commit("sbSuccess", "Estimate approved.", { root: true });
        }
        await dispatch("loadEstimates", {
          projectId,
          cycleId: projectId,
          page: 1,
          limit: 500,
          filter: "all",
        });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async submitEstimates({ commit, rootState, dispatch }, { projectId, items }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/estimates/submit?cid=${cid}`, {
          project_id: projectId,
          items,
        });
        const data = unwrap(res) || {};
        const failed = data.failed || data.failures || [];
        if (failed.length) {
          const message = buildBulkPartialFailureMessage(
            failed.map((entry) => entry?.reference || estimateRefLabel(entry))
          );
          commit("sbError", message, { root: true });
        } else {
          commit("sbSuccess", "Estimate submitted for approval.", { root: true });
        }
        await dispatch("loadEstimates", {
          projectId,
          cycleId: projectId,
          page: 1,
          limit: 500,
          filter: "all",
        });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async addEstimateNote({ commit, rootState }, { projectId, items }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/estimates/note?cid=${cid}`, {
          project_id: projectId,
          items,
        });
        return unwrap(res) || {};
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async rejectEstimates({ commit, rootState, dispatch }, { projectId, items, reason }) {
      const reasonError = validateRejectReason(reason);
      if (reasonError) {
        commit("setError", reasonError);
        commit("sbError", reasonError, { root: true });
        const reported = new Error(reasonError);
        reported.__accrualsReported = true;
        throw reported;
      }
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const remark = String(reason).trim();
        const res = await getAxios().post(`${API}/estimates/reject?cid=${cid}`, {
          project_id: projectId,
          items: (items || []).map((item) => ({ ...item, reason: remark })),
        });
        const data = unwrap(res) || {};
        const failed = data.failed || data.failures || [];
        if (failed.length) {
          const message = buildBulkPartialFailureMessage(
            failed.map((entry) => entry?.reference || estimateRefLabel(entry))
          );
          commit("sbError", message, { root: true });
        } else {
          commit("sbSuccess", "Estimate rejected with reason.", { root: true });
        }
        await dispatch("loadEstimates", {
          projectId,
          cycleId: projectId,
          page: 1,
          limit: 500,
          filter: "all",
        });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async excludeEstimates({ commit, rootState, dispatch }, { projectId, items, reason }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const remark = String(reason || '').trim();
        const res = await getAxios().post(`${API}/estimates/exclude?cid=${cid}`, {
          project_id: projectId,
          items: (items || []).map((item) => ({
            ...item,
            reason: remark,
            note: remark,
          })),
        });
        const data = unwrap(res) || {};
        const failed = data.failed || data.failures || [];
        if (failed.length) {
          const message = buildBulkPartialFailureMessage(
            failed.map((entry) => entry?.reference || estimateRefLabel(entry))
          );
          commit("sbError", message, { root: true });
        } else {
          commit("sbSuccess", "Estimate excluded from this cycle.", { root: true });
        }
        await dispatch("loadEstimates", {
          projectId,
          cycleId: projectId,
          page: 1,
          limit: 500,
          filter: "all",
        });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async includeEstimates({ commit, rootState, dispatch }, { projectId, items, reason }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const remark = String(reason || '').trim();
        const res = await getAxios().post(`${API}/estimates/include?cid=${cid}`, {
          project_id: projectId,
          items: (items || []).map((item) => ({
            ...item,
            reason: remark,
            note: remark,
          })),
        });
        const data = unwrap(res) || {};
        const failed = data.failed || data.failures || [];
        if (failed.length) {
          const message = buildBulkPartialFailureMessage(
            failed.map((entry) => entry?.reference || estimateRefLabel(entry))
          );
          commit("sbError", message, { root: true });
        } else {
          commit("sbSuccess", "Estimate included back in this cycle.", { root: true });
        }
        await dispatch("loadEstimates", {
          projectId,
          cycleId: projectId,
          page: 1,
          limit: 500,
          filter: "all",
        });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async resubmitEstimates({ commit, rootState, dispatch }, { projectId, items }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/estimates/resubmit?cid=${cid}`, {
          project_id: projectId,
          items,
        });
        const data = unwrap(res) || {};
        const failed = data.failed || data.failures || [];
        if (failed.length) {
          const message = buildBulkPartialFailureMessage(
            failed.map((entry) => entry?.reference || estimateRefLabel(entry))
          );
          commit("sbError", message, { root: true });
        } else {
          commit("sbSuccess", "Estimate resubmitted for approval.", { root: true });
        }
        await dispatch("loadEstimates", {
          projectId,
          cycleId: projectId,
          page: 1,
          limit: 500,
          filter: "all",
        });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async approveAllEstimates({ commit, rootState, dispatch }, { projectId, items }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/estimates/approve-all?cid=${cid}`, {
          project_id: projectId,
          items,
        });
        const data = unwrap(res) || {};
        const failed = data.failed || data.failures || [];
        if (failed.length) {
          const message = buildBulkPartialFailureMessage(
            failed.map((entry) => entry?.reference || estimateRefLabel(entry))
          );
          commit("sbError", message, { root: true });
        } else {
          commit("sbSuccess", "All selected estimates approved.", { root: true });
        }
        await dispatch("loadEstimates", {
          projectId,
          cycleId: projectId,
          page: 1,
          limit: 500,
          filter: "all",
        });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async requestEstimateResubmission({ commit, rootState, dispatch }, { projectId, items, reason }) {
      const reasonError = validateRejectReason(reason);
      if (reasonError) {
        commit("setError", reasonError);
        commit("sbError", reasonError, { root: true });
        const reported = new Error(reasonError);
        reported.__accrualsReported = true;
        throw reported;
      }
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const remark = String(reason).trim();
        const res = await getAxios().post(`${API}/estimates/request-resubmission?cid=${cid}`, {
          project_id: projectId,
          items: (items || []).map((item) => ({ ...item, reason: remark })),
        });
        const data = unwrap(res) || {};
        const failed = data.failed || data.failures || [];
        if (failed.length) {
          const message = buildBulkPartialFailureMessage(
            failed.map((entry) => entry?.reference || estimateRefLabel(entry))
          );
          commit("sbError", message, { root: true });
        } else {
          commit("sbSuccess", "Resubmission requested — the line is back with the preparer.", { root: true });
        }
        await dispatch("loadEstimates", {
          projectId,
          cycleId: projectId,
          page: 1,
          limit: 500,
          filter: "all",
        });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async assignEstimateGl({ commit, rootState, dispatch }, { projectId, items }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/estimates/gl?cid=${cid}`, {
          project_id: projectId,
          items,
        });
        const data = unwrap(res) || {};
        const failed = data.failed || data.failures || [];
        if (failed.length) {
          const message = buildBulkPartialFailureMessage(
            failed.map((entry) => entry?.reference || estimateRefLabel(entry))
          );
          commit("sbError", message, { root: true });
        } else {
          commit("sbSuccess", "Estimate recorded", { root: true });
        }
        await dispatch("loadEstimates", {
          projectId,
          cycleId: projectId,
          page: 1,
          limit: 500,
          filter: "all",
        });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async unlockEstimates({ commit, rootState, dispatch }, { projectId, items }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/estimates/unlock?cid=${cid}`, {
          project_id: projectId,
          items,
        });
        const data = unwrap(res) || {};
        commit("sbSuccess", "Estimate unlocked", { root: true });
        await dispatch("loadEstimates", {
          projectId,
          cycleId: projectId,
          page: 1,
          limit: 500,
          filter: "all",
        });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async fetchEstimateDetail({ rootState }, { projectId, estimateId, lineKey }) {
      const params = {
        project_id: projectId,
        estimate_id: estimateId,
      };
      if (lineKey) params.line_key = lineKey;
      const res = await storeGet(getAxios(), rootState, `${API}/estimates/detail`, params);
      return unwrap(res) || {};
    },
    async loadEstimates(
      { commit, rootState },
      { projectId, cycleId, page = 1, limit = 500, filter = "all", search = "" }
    ) {
      commit("setEstimatesLoading", true);
      commit("setError", null);
      try {
        const params = {
          project: projectId,
          page,
          limit,
          filter,
        };
        if (cycleId) params.cycle_id = cycleId;
        if (search) params.search = search;
        const res = await storeGet(getAxios(), rootState, `${API}/estimates`, params);
        const data = unwrap(res) || {};
        commit("setEstimates", {
          rows: data.rows || [],
          total: data.total || 0,
          counts: data.counts || {
            all: 0,
            pending_submission: 0,
            pending_approval: 0,
            approved: 0,
            rejected: 0,
            excluded: 0,
          },
          page: data.page || page,
          limit: data.limit || limit,
        });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      } finally {
        commit("setEstimatesLoading", false);
      }
    },
    async loadSchedule({ commit, rootState }, { projectId }) {
      commit("setScheduleLoading", true);
      commit("setError", null);
      try {
        const res = await storeGet(getAxios(), rootState, `${API}/schedule`, {
          project: projectId,
        });
        const data = unwrap(res) || {};
        commit("setSchedule", data);
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      } finally {
        commit("setScheduleLoading", false);
      }
    },
    async prepareSchedule({ commit, rootState }, { projectId }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/schedule/prepare?cid=${cid}`, {
          project_id: projectId,
        });
        const data = unwrap(res) || {};
        commit("setSchedule", data);
        commit("sbSuccess", "Schedule prepared", { root: true });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },
    async runInferenceBatch({ commit, rootState }, { projectId, cycleId, requestIds }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const body = { project_id: projectId };
        if (cycleId) body.cycle_id = cycleId;
        if (Array.isArray(requestIds) && requestIds.length) {
          body.request_ids = requestIds;
        }
        const res = await getAxios().post(`${API}/inference/run?cid=${cid}`, body);
        const data = unwrap(res) || {};
        commit("sbSuccess", "Accruals inference queued", { root: true });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },

    async loadVendorMasterOptions({ rootState }, { projectId }) {
      try {
        const res = await storeGet(getAxios(), rootState, `${API}/vendorOptions`, {
          project: projectId,
        });
        const data = unwrap(res) || {};
        const vendors = Array.isArray(data) ? data : data.vendors || [];
        return vendors
          .map((row) => ({
            id: String(row.id || row.vendor_id || row.supplier_id || "").trim(),
            name: String(row.name || row.vendor_name || row.supplier_name || "").trim(),
          }))
          .filter((row) => row.id)
          .map((row) => ({ id: row.id, name: row.name || row.id }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (err) {
        console.log("[accruals]: loadVendorMasterOptions failed", err);
        return [];
      }
    },

    async loadGlOptions({ commit, state, rootState }) {
      if ((state.glOptions || []).length) return state.glOptions;
      try {
        const res = await storeGet(getAxios(), rootState, `${API}/glOptions`, {});
        const data = unwrap(res) || {};
        const accounts = Array.isArray(data) ? data : (data.accounts || []);
        const options = accounts
          .map((row) => (
            typeof row === "string"
              ? formatGlOptionLabel(row)
              : formatGlOptionLabel(row?.glcode || row?.code || row?.label, row?.name)
          ))
          .filter(Boolean);
        commit("setGlOptions", options);
        return options;
      } catch (err) {
        console.log("[accruals]: loadGlOptions failed", err);
        commit("setGlOptions", []);
        return [];
      }
    },

    async loadLocationMaster({ commit, state }) {
      if ((state.locationMaster || []).length) return state.locationMaster;
      try {
        const res = await getAxios().post('/v2/financialclose/location/getenabledlocation');
        const body = res?.data || {};
        const list = Array.isArray(body.data) ? body.data : (body.data?.locations || []);
        const options = list
          .map((row) => ({
            id: row?._id || row?.id || null,
            code: row?.code || row?.subsidiary_code || null,
            subsidiary_code: row?.subsidiary_code || null,
            subsidiary: row?.subsidiary || null,
            name: row?.name || row?.description || null,
          }))
          .filter((row) => row.name);
        commit('setLocationMaster', options);
        return options;
      } catch (err) {
        console.log('[accruals]: loadLocationMaster failed', err);
        commit('setLocationMaster', []);
        return [];
      }
    },

    async loadComputationTemplates({ commit, rootState }, { enabled } = {}) {
      try {
        const params = {};
        if (enabled === true || enabled === false) params.enabled = enabled;
        else params.enabled = true;
        const res = await storeGet(getAxios(), rootState, `${API}/computationTemplates`, params);
        const data = unwrap(res) || {};
        const templates = Array.isArray(data) ? data : (data.templates || []);
        commit("setComputationTemplates", templates);
        return templates;
      } catch (err) {
        console.log("[accruals]: loadComputationTemplates failed", err);
        commit("setComputationTemplates", []);
        return [];
      }
    },

    async loadComputationRules({ commit, rootState }, { projectId }) {
      commit("setComputationRuleLoading", true);
      commit("setError", null);
      try {
        const res = await storeGet(getAxios(), rootState, `${API}/computationRules`, {
          project: projectId,
        });
        const data = unwrap(res) || {};
        const rules = Array.isArray(data) ? data : (data.rules || data.rows || []);
        commit("setComputationRules", rules);
        return rules;
      } catch (err) {
        if (err?.response?.status === 404) {
          commit("setComputationRules", []);
          return [];
        }
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        throw err;
      } finally {
        commit("setComputationRuleLoading", false);
      }
    },

    async saveComputationRule({ commit, rootState }, { projectId, rule, confirm: confirmFlag, silent }) {
      commit("setComputationRuleSaving", true);
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const { execution_sequence: sequence, _id, ...rest } = rule || {};
        delete rest.__draftId;
        const body = { project_id: projectId, ...rest };
        if (_id) body._id = _id;
        if (Number(sequence) >= 1) body.execution_sequence = sequence;
        if (confirmFlag) body.confirm = true;
        const res = await getAxios().put(`${API}/computationRules?cid=${cid}`, body);
        const data = unwrap(res) || {};
        const saved = data.rule || data;
        const { rerun_required: rerunRequired, affected_count: affectedCount, ...ruleOnly } = saved || {};
        commit("upsertComputationRule", ruleOnly);
        if (!silent) commit("sbSuccess", "Estimation rule saved", { root: true });
        return { ...ruleOnly, rerun_required: rerunRequired, affected_count: affectedCount };
      } catch (err) {
        const details = err?.response?.data?.data;
        const first = Array.isArray(details) ? details[0] : details;
        if (err?.response?.status === 409 && first?.requires_confirmation) {
          return {
            requires_confirmation: true,
            affected_count: first.affected_count,
            message: envelopeError(err),
          };
        }
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        throw err;
      } finally {
        commit("setComputationRuleSaving", false);
      }
    },

    async reorderComputationRules({ commit, rootState }, { projectId, orderedIds }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().put(`${API}/computationRules/reorder?cid=${cid}`, {
          project_id: projectId,
          ordered_ids: orderedIds,
        });
        const data = unwrap(res) || {};
        const rules = Array.isArray(data) ? data : (data.rules || data.rows || []);
        if (rules.length) commit("setComputationRules", rules);
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },

    async disableComputationRule({ commit, rootState }, { projectId, ruleId }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/computationRules/${ruleId}/disable?cid=${cid}`, {
          project_id: projectId,
          rule_id: ruleId,
        });
        const data = unwrap(res) || {};
        const updated = data.rule || data;
        if (updated && updated._id) commit("upsertComputationRule", updated);
        commit("sbSuccess", "Rule disabled", { root: true });
        return updated;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },

    async enableComputationRule({ commit, rootState }, { projectId, ruleId }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/computationRules/${ruleId}/enable?cid=${cid}`, {
          project_id: projectId,
          rule_id: ruleId,
        });
        const data = unwrap(res) || {};
        const updated = data.rule || data;
        if (updated && updated._id) commit("upsertComputationRule", updated);
        commit("sbSuccess", "Rule enabled", { root: true });
        return updated;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },

    async deleteComputationRule({ commit, rootState }, { projectId, ruleId }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        await getAxios().delete(`${API}/computationRules/${ruleId}?cid=${cid}&project=${projectId}`, {
          data: { project_id: projectId },
        });
        commit("removeComputationRule", ruleId);
        commit("sbSuccess", "Rule deleted", { root: true });
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },

    async loadRerunFreshness({ commit, rootState }, { projectId }) {
      if (!projectId) return null;
      try {
        const res = await storeGet(getAxios(), rootState, `${API}/rerun/freshness`, {
          project: projectId,
        });
        const data = unwrap(res) || {};
        commit("setRerunFreshness", data);
        return data;
      } catch (err) {
        console.log("[accruals]: loadRerunFreshness failed", err);
        return null;
      }
    },

    async startAccrualsRerun({ commit, rootState }, { projectId, scenario }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/rerun/start?cid=${cid}`, {
          project_id: projectId,
          scenario,
          confirm: true,
        });
        const data = unwrap(res) || {};
        commit("sbSuccess", "Rerun started — estimates were reset for re-evaluation", { root: true });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },

    async startComputationRun({ commit, rootState }, { projectId }) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().post(`${API}/computationRuns/start?cid=${cid}`, {
          project_id: projectId,
        });
        const data = unwrap(res) || {};
        commit("setComputationRun", data);
        commit("sbSuccess", "Estimation run started", { root: true });
        return data;
      } catch (err) {
        if (isFlowInProgressError(err)) {
          const message = envelopeError(err);
          commit("sbError", message, { root: true });
          return { skipped: true, reason: "locked", message };
        }
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        throw err;
      }
    },

    async loadComputationRunStatus({ commit, rootState }, { projectId, runId }) {
      commit("setError", null);
      try {
        const params = { project: projectId };
        if (runId) params.run_id = runId;
        const res = await storeGet(getAxios(), rootState, `${API}/computationRuns/status`, params);
        const data = unwrap(res) || {};
        commit("setComputationRun", data);
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        throw err;
      }
    },

    async loadIngestionConfig({ commit, rootState }) {
      commit("setError", null);
      try {
        const res = await storeGet(getAxios(), rootState, `${API}/ingestionConfig`, {});
        return unwrap(res) || {};
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        throw err;
      }
    },

    async saveIngestionConfig({ commit, rootState }, body) {
      commit("setError", null);
      try {
        const cid = resolveCid(rootState);
        const res = await getAxios().put(`${API}/ingestionConfig?cid=${cid}`, body);
        const data = unwrap(res) || {};
        commit("sbSuccess", "Ingestion configuration saved", { root: true });
        return data;
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        commit("sbError", message, { root: true });
        if (err && typeof err === "object") err.__accrualsReported = true;
        throw err;
      }
    },

    async loadIngestionConfigHistory({ commit, rootState }) {
      commit("setError", null);
      try {
        const res = await storeGet(getAxios(), rootState, `${API}/ingestionConfig/history`, {});
        return unwrap(res) || { versions: [] };
      } catch (err) {
        const message = envelopeError(err);
        commit("setError", message);
        throw err;
      }
    },
  },
};
